import React, { useEffect, useRef } from 'react';
import { ExternalLink, Github, Play, Sparkles } from 'lucide-react';
import GlassCard from './ui/GlassCard';
import ScrollReveal from './ui/ScrollReveal';
import { trackEvent } from '../utils/analytics';

type Product = {
  name: string;
  tagline: string;
  description: string;
  category: string;
  techStack: string[];
  features: string[];
  links: {
    live?: string;
    github?: string;
    demo?: string;
  };
  status: 'active' | 'maintained' | 'archived';
  logo: string;
  accentColor: string;
};

export const Products: React.FC = () => {
  const productRefs = useRef<(HTMLDivElement | null)[]>([]);
  const viewedProducts = useRef<Set<number>>(new Set());

  const products: Product[] = [
    {
      name: 'ProDevs',
      tagline: 'Technology Platform & Blog',
      description: 'ProDevs is a comprehensive technology platform and blog providing expert insights, programming tutorials, technical articles, video tutorials, and automated newsletter delivery for tech enthusiasts. Stay ahead with ProDevs insights on programming languages, AI advancements, and cutting-edge technology trends.',
      category: 'Platform',
      techStack: ['React', 'Next.js', 'Node.js', 'AI/ML'],
      features: [
        'Expert insights from professional developers',
        'Video tutorials and technical blogs',
        'Automated newsletter delivery',
        'Programming tutorials and articles',
        'Technology trends and AI advancements'
      ],
      links: {
        live: 'https://prodevs.in'
      },
      status: 'active',
      logo: '/prodevs-logo.jpg',
      accentColor: 'from-blue-500/20 to-purple-500/20'
    },
    {
      name: 'Talk2Term',
      tagline: 'Stop Googling CLI Flags',
      description: 'Talk2Term is a ZSH plugin that converts plain English into perfect shell commands using AI. This terminal AI tool enables CLI automation without memorizing commands. Stop googling CLI flags—just type in English and get executable commands. Features GPT-powered command generation, free daily credits, and native ZSH integration for natural language terminal automation.',
      category: 'AI Tools',
      techStack: ['ZSH', 'Python', 'OpenAI API', 'Shell Scripting'],
      features: [
        'GPT-powered command generation',
        'Free daily credits (5 Lite requests)',
        'Native ZSH integration',
        'Convert English to shell commands',
        'Natural language terminal automation',
        'AI terminal assistant'
      ],
      links: {
        live: 'https://talk2term.prodevs.in',
        github: 'https://github.com/prodavalkot/zsh-talk2term'
      },
      status: 'active',
      logo: '/prodevs-logo.jpg',
      accentColor: 'from-green-500/20 to-emerald-500/20'
    },
    {
      name: 'MegaSearch',
      tagline: 'Search Smarter, Not Harder',
      description: 'MegaSearch is an intelligent metasearch engine that queries multiple sources simultaneously, analyzes results with AI, and synthesizes comprehensive answers with citations. Search smarter with parallel execution across 10+ search sources, AI-powered analysis, iterative refinement, and native MCP server support for AI agents. Perfect for research, development, and enterprise use cases.',
      category: 'AI Tools',
      techStack: ['Python', 'FastAPI', 'LangChain', 'Vector DBs', 'MCP'],
      features: [
        'Parallel execution across 10+ search sources',
        'AI-powered analysis and refinement',
        'Native MCP server support',
        'Synthesized answers with citations',
        'Under 3 seconds average response time',
        '5x refinement loops for perfect results',
        'Enterprise ready search API'
      ],
      links: {
        live: 'https://megasearch.prodevs.in'
      },
      status: 'active',
      logo: '/megasearch-logo.png',
      accentColor: 'from-orange-500/20 to-red-500/20'
    },
    {
      name: "ProDev's News",
      tagline: 'AI News Aggregator',
      description: "ProDev's News is a personalized AI/ML news digest automatically aggregated and deduplicated from 50+ trusted sources. This AI news aggregator features smart deduplication, personalized news feeds, audio podcast summaries, and real-time updates. Stay ahead in AI without the noise with curated technology news for developers covering LLMs, robotics, computer vision, and AI research.",
      category: 'AI Tools',
      techStack: ['Python', 'AI/ML', 'Web Scraping', 'Deduplication'],
      features: [
        '50+ trusted news sources',
        'Smart deduplication',
        'Personalized news feed',
        'Audio podcast summaries',
        'Real-time AI developments',
        'AI summary for every article',
        '1K+ articles per day'
      ],
      links: {
        live: 'https://news.prodevs.in'
      },
      status: 'active',
      logo: '/prodevs-news-logo.png',
      accentColor: 'from-cyan-500/20 to-blue-500/20'
    },
    {
      name: 'MeetEcho',
      tagline: 'AI-Powered Transcription Platform',
      description: 'MeetEcho is an AI-powered transcription platform that transforms audio and video files into actionable insights. Transcribe with 99% accuracy in 50+ languages using OpenAI Whisper, chat with your content using AI, generate intelligent summaries with customizable templates, and extract text from video frames using GPT-4 Vision. Features BYOK security for complete data privacy.',
      category: 'SaaS',
      techStack: ['React', 'OpenAI Whisper', 'GPT-4', 'Vector DBs'],
      features: [
        'Audio & video transcription (50+ languages)',
        'Chat with your content using AI',
        'Smart summaries with customizable templates',
        '99% accuracy transcription',
        'Under 30 seconds processing',
        'Video frame analysis with GPT-4 Vision',
        'BYOK security for data privacy'
      ],
      links: {
        live: 'https://meetecho.prodevs.in'
      },
      status: 'active',
      logo: '/meetecho-logo.png',
      accentColor: 'from-pink-500/20 to-rose-500/20'
    },
    {
      name: 'RitualRhythms',
      tagline: 'Society Management Platform',
      description: 'RitualRhythms is a comprehensive society management platform streamlining operations, enhancing resident experience, and building stronger communities. Used by 500+ active societies across India, it features event management, smart QR-based food coupons, donation management, member directory, analytics & reports, and 99.9% uptime. Transform your society management with this community management platform.',
      category: 'SaaS',
      techStack: ['React', 'Node.js', 'PostgreSQL', 'Mobile App'],
      features: [
        'Event management and smart food coupons',
        'Donation management and member directory',
        'Analytics & reports',
        'QR-based food distribution system',
        '500+ active societies',
        '50K+ happy residents',
        '1M+ events managed',
        '99.9% uptime'
      ],
      links: {
        live: 'https://ritualrhythms.in'
      },
      status: 'active',
      logo: '/ritualrhythms-logo.png',
      accentColor: 'from-indigo-500/20 to-purple-500/20'
    },
    {
      name: 'MySmartNest',
      tagline: 'Smart Home Automation',
      description: 'MySmartNest offers premium smart home automation solutions through CleverCasa, combining cutting-edge technology with intuitive design. Transform your home with intelligent automation featuring enhanced security with motion detection, energy efficiency optimization, and complete home control from anywhere. Premium IoT home automation for modern living in India.',
      category: 'IoT/Smart Home',
      techStack: ['IoT', 'Home Automation', 'Mobile App', 'Cloud'],
      features: [
        'Enhanced security with motion detection',
        'Energy efficiency optimization',
        'Complete home control from anywhere',
        'Premium smart home solutions',
        'CleverCasa luxury automation',
        'Professional installation support',
        'Reduce electricity bills by up to 30%'
      ],
      links: {
        live: 'https://mysmartnest.in'
      },
      status: 'active',
      logo: '/mysmartnest-logo.png',
      accentColor: 'from-amber-500/20 to-yellow-500/20'
    }
  ];

  useEffect(() => {
    const observers = productRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !viewedProducts.current.has(index)) {
              const productName = products[index]?.name.toLowerCase().replace(/\s+/g, '-');
              if (productName) {
                trackEvent(`product-view-${productName}`);
                viewedProducts.current.add(index);
              }
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, [products]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/40';
      case 'maintained':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40';
      case 'archived':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/40';
      default:
        return 'bg-white/10 text-ink-dim border-white/20';
    }
  };

  return (
    <section id="products" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ink">Products & Startups Developed by Sudipta Dhara</h2>
          <div className="w-24 h-1 bg-brand mx-auto"></div>
          <p className="text-xs text-ink-dim mt-2">Last updated: December 2025</p>
          <p className="text-ink-dim mt-4 max-w-3xl mx-auto">
            Innovative technology products including AI-powered tools, SaaS platforms, and smart home solutions created by Sudipta Dhara. 
            Built with expertise from <a href="#experience" className="text-brand hover:underline">14+ years of software development experience</a> and <a href="#skills" className="text-brand hover:underline">advanced skills in generative AI and backend development</a>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => {
            const productName = product.name.toLowerCase().replace(/\s+/g, '-');
            return (
              <ScrollReveal key={index}>
                <GlassCard 
                  ref={(el) => {
                    productRefs.current[index] = el;
                  }}
                  className={`h-full flex flex-col relative overflow-hidden group hover:shadow-lux-2 transition-all duration-300 hover:-translate-y-2`}
                >
                {/* Accent gradient background */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${product.accentColor} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-0`} />
                
                {/* Top accent border */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${product.accentColor.replace('/20', '')} opacity-60`} />
                
                <div className="flex-1 relative z-10">
                  {/* Header with Logo */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lgx bg-gradient-to-br ${product.accentColor} border border-white/20 flex-shrink-0 flex items-center justify-center`}>
                        <img 
                          src={product.logo} 
                          alt={`${product.name} logo - ${product.tagline}`}
                          width={24}
                          height={24}
                          className="w-6 h-6 object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl md:text-2xl font-bold mb-1 text-ink">
                          {product.name}
                        </h3>
                        <p className="text-sm text-ink-dim mb-2">{product.tagline}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(product.status)}`}>
                      {product.status}
                    </span>
                  </div>

                  {/* Category */}
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-brand/20 text-brand border border-brand/40">
                      {product.category}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-ink-dim mb-5 text-sm leading-relaxed">{product.description}</p>

                  {/* Features */}
                  <div className="mb-5">
                    <h4 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                      <Sparkles size={14} className="text-brand" />
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {product.features.map((feature, i) => (
                        <li key={i} className="text-xs text-ink-dim flex items-start gap-2">
                          <span className="text-brand mt-1.5 flex-shrink-0">▸</span>
                          <span className="leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {product.techStack.map((tech, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 rounded-md text-xs bg-white/5 text-ink-dim border border-white/10 hover:border-brand/40 hover:text-brand transition-colors"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Links */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10 relative z-10">
                  {product.links.live && (
                    <a
                      href={product.links.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium bg-brand/20 text-brand hover:bg-brand/30 border border-brand/40 transition-all hover:scale-105"
                      onClick={() => trackEvent(`product-${productName}-live`)}
                      data-umami-event={`product-${productName}-live`}
                    >
                      <ExternalLink size={12} />
                      Live Site
                    </a>
                  )}
                  {product.links.github && (
                    <a
                      href={product.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium bg-white/5 text-ink hover:bg-white/10 border border-white/10 transition-all hover:scale-105"
                      onClick={() => trackEvent(`product-${productName}-github`)}
                      data-umami-event={`product-${productName}-github`}
                    >
                      <Github size={12} />
                      GitHub
                    </a>
                  )}
                  {product.links.demo && (
                    <a
                      href={product.links.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium bg-white/5 text-ink hover:bg-white/10 border border-white/10 transition-all hover:scale-105"
                      onClick={() => trackEvent(`product-${productName}-demo`)}
                      data-umami-event={`product-${productName}-demo`}
                    >
                      <Play size={12} />
                      Demo
                    </a>
                  )}
                </div>
              </GlassCard>
            </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

